import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { PromptModalType } from "enums/questionnaire";
import { useModalOpener } from "hooks/helpers/useModalOpener";
import { useDispatch } from "react-redux";
import { useAppSelector } from "redux/hooks";
import { setAnswers } from "redux/reducers/answers";
import { selectCurrentNavigation } from "redux/reducers/navigation";
import { checkDepends } from "utils/new-questionnaire";
import { removeEmojis } from "utils/strings";

import { categories, Depend, Question, Step } from "../../constants/questions";
import PromptModal from "../Shared/PromptModal";
import SuggestionBox from "../Shared/SuggestionBox";

type SelectManyOptionQuestionProps = {
    data: Question;
    onAnswer: (_question: string, _answer: string[] | undefined) => void;
    initialValue?: any;
    categoryAnswers?: Record<string, any>;
    handlePrev: () => void;
    suggestions?: any;
};

const SelectManyOptionQuestion: FC<SelectManyOptionQuestionProps> = ({
    data,
    onAnswer,
    handlePrev,
    initialValue,
    categoryAnswers,
    suggestions,
}) => {
    const dispatch = useDispatch();
    const { stepLoading, currentCategory } = useAppSelector(selectCurrentNavigation);

    const [selectedOptions, setSelectedOptions] = useState<string[]>(initialValue || []);
    const [showModalOpener, setShowModalOpener] = useState(false);
    const [suggestionDismissed, setSuggestionDismissed] = useState(false);
    const { answers } = useAppSelector(state => state.answers);

    const promptModalData = useMemo(() => ({
        type: data.promptModal?.type || PromptModalType.info,
        title: data.promptModal?.title || "",
        text: data.promptModal?.text || "",
        image: data.promptModal?.image || "",
        promptActionButtonText: data.promptModal?.promptActionButtonText || "",
        promptActionButtonUrl: data.promptModal?.promptActionButtonUrl || "",
    }), [data.promptModal]);

    const {
        isOpened: isQuestionHelpModalOpened,
        open: handleQuestionHelpOpenModal,
        close: handleQuestionHelpCloseModal,
    } = useModalOpener();

    const handleOptionChange = (value: string) => {
        if (stepLoading) return;

        const categorySteps = categories[currentCategory].steps;
        const categoryTitle = categories[currentCategory].title;
        const isRemoving = selectedOptions.includes(value);

        const updatedOptions = isRemoving
            ? selectedOptions.filter(option => option !== value)
            : [...selectedOptions, value];

        setSelectedOptions(updatedOptions);

        if (isRemoving && (data.id === "GENERAL_OPTIONS_APPLY" || data.id === "STATE_BENEFITS_OPTIONS_APPLY" || data.id === "PENSION_INCOME_OPTIONS_APPLY")) {
            // Clone answers to avoid modifying Redux state directly
            const newAnswers = structuredClone(answers);

            // Function to clear all answers in a step
            const clearStepAnswers = (step: Step, categoryTitle: string) => {
                // Clear all questions in the step
                step.questions?.forEach(question => {
                    if (newAnswers[categoryTitle]?.[question.question]) {
                        delete newAnswers[categoryTitle][question.question];
                    }
                });
            };

            // Recursive function to clear answers from dependent questions
            const clearDependentAnswers = (questionsOrCards: any[], categoryTitle: string) => {
                questionsOrCards.forEach((item: any) => {
                    if (item.depend && item.depend.some((dep: Depend) => dep.question === data.question && dep.answer === value)) {
                        if (newAnswers[categoryTitle]?.[item.question]) {
                            delete newAnswers[categoryTitle][item.question]; // 🔥 Directly delete from object
                        }

                        // Recursively clear nested questions inside questionsList types
                        if (item.questions && item.questions.length > 0) {
                            clearDependentAnswers(item.questions, categoryTitle);
                        }
                    }
                });
            };

            // Iterate through all steps
            categorySteps.forEach((step: Step) => {
                // ✅ **NEW: Clear entire step if it has depend[] and the condition is broken**
                if (
                    step.depend &&
                    step.depend.some((dep: Depend) => dep.question === data.question && dep.answer === value)
                ) {
                    clearStepAnswers(step, categoryTitle);
                } else {
                    // If not clearing whole step, clear dependent answers in questions
                    clearDependentAnswers(step.questions ?? [], categoryTitle);
                }
            });
            // Ensure we update the answers properly
            newAnswers[categoryTitle] = {
                ...newAnswers[categoryTitle],
                [data.question]: updatedOptions, // Update selected options
            };
            // Dispatch the latest updated state
            dispatch(setAnswers(newAnswers));
            return;
        }

        onAnswer(data.question, updatedOptions);
    };

    const checkModalDepends = useCallback((depend: Depend[], categoryAnswers?: Record<string, any>) => {
        if (categoryAnswers) {
            return checkDepends(depend, categoryAnswers);
        } else {
            return false;
        }
    }, []);

    const relevantSuggestion = useMemo(() => {
        if (!Array.isArray(suggestions)) return null;
        return suggestions.find(
            s =>
                s?.supportingData?.transactions?.length > 0);
    }, [suggestions]);

    useEffect(() => {
        setSelectedOptions(initialValue || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValue]);

    useEffect(() => {
        if (data?.promptModal?.depend && categoryAnswers) {
            const shouldModalShow = checkModalDepends(data.promptModal?.depend, categoryAnswers);
            setShowModalOpener(shouldModalShow);
            if (shouldModalShow && !data.promptModal?.manualOpen) {
                handleQuestionHelpOpenModal();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryAnswers]);

    return (
        <div className="new-questionnaire__question">
            {data.questionTitle && (
                <div className="new-questionnaire__list-question-title" >{data.questionTitle}</div>
            )}

            {!suggestionDismissed && relevantSuggestion && (
                <SuggestionBox
                    suggestion={relevantSuggestion}
                    onDismiss={() => setSuggestionDismissed(true)}
                />
            )}

            <div className="new-questionnaire__question-text" style={data.questionTitle ? { margin: "4px auto" } : {}}>
                <span>{data.question}
                    {showModalOpener && (
                        <span className="shared__learn-more" onClick={handleQuestionHelpOpenModal}>
                        Learn more
                        </span>
                    )} </span>
            </div>

            {data.infoText && <div className="new-questionnaire__info-text">{data.infoText}</div>}

            <div className="new-questionnaire__select-many-tiles__question-options mt10">
                {data?.options?.map((value, index) => (
                    <div
                        key={index}
                        className={`new-questionnaire__select-many-tiles__question-option${selectedOptions?.includes(value) ? " selected" : stepLoading ? " disabled" : ""}`}
                        onClick={() => handleOptionChange(value)}
                    >
                        {data.iconMapping && (
                            <div>
                                <img width={30} height={30} src={data.iconMapping[value]} alt="icon" style={{ display: "block" }} />
                            </div>
                        )}
                        <div className="new-questionnaire__select-many-tiles__text">
                            {data.iconMapping ? removeEmojis(value) : value}
                        </div>
                    </div>
                ))}
            </div>
            {isQuestionHelpModalOpened && (
                <PromptModal
                    isOpen={isQuestionHelpModalOpened}
                    closeModal={handleQuestionHelpCloseModal}
                    handlePrev={handlePrev}
                    data={promptModalData}
                />
            )}
        </div>
    );
};

export default SelectManyOptionQuestion;
